import { faCheck } from "@fortawesome/pro-light-svg-icons";
import { faCopy, faMap } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    type ReactNode,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import { ROUTES } from "../routes";

interface CopyButtonProps {
    value: string;
    title?: string;
    className?: string;
}

/**
 * Copy a value to the clipboard, confirming with a checkmark for a moment.
 */
export const CopyButton = (props: CopyButtonProps) => {
    const [copied, setCopied] = useState(false);
    const timeout = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => () => clearTimeout(timeout.current), []);

    const copy = useCallback(() => {
        navigator.clipboard.writeText(props.value).then(() => {
            setCopied(true);
            clearTimeout(timeout.current);
            timeout.current = setTimeout(() => setCopied(false), 2000);
        });
    }, [props.value]);

    return (
        <button
            type="button"
            title={props.title || "Copy to clipboard"}
            onClick={copy}
            className={`shrink-0 hover:text-blue-700 ${props.className || ""}`}
        >
            <FontAwesomeIcon
                icon={copied ? faCheck : faCopy}
                className={copied ? "text-green-600" : undefined}
            />
        </button>
    );
};

interface CoordinatesFieldProps {
    coordinates?: number[];
    allowCopy?: boolean;
    mapLinkParams?: {
        lat?: string;
        lon?: string;
        emissionId?: string;
        siteId?: string;
    };
}

export const CoordinatesField = (props: CoordinatesFieldProps) => {
    return (
        <div className="flex items-center">
            {props.coordinates ? (
                <>
                    {props.coordinates[1].toFixed(5)}
                    {", "}
                    {props.coordinates[0].toFixed(5)}
                </>
            ) : (
                "-"
            )}
            {props.mapLinkParams && (
                <button
                    type="button"
                    title="Open on main map"
                    className="ml-1 hover:text-blue-700 cursor-pointer"
                    onClick={(e) => {
                        e.stopPropagation();
                        const params = new URLSearchParams(
                            props.mapLinkParams,
                        ).toString();
                        const url = `${ROUTES.MAP}?${params}`;
                        window.open(url, "_blank", "noopener,noreferrer");
                    }}
                >
                    <FontAwesomeIcon icon={faMap} />
                </button>
            )}
            {props.allowCopy && (
                <button
                    type="button"
                    title="Copy to clipboard"
                    className="ml-1 hover:text-blue-700 cursor-pointer"
                    onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(
                            `${props.coordinates[1]}, ${props.coordinates[0]}`,
                        );
                    }}
                >
                    <FontAwesomeIcon icon={faCopy} />
                </button>
            )}
        </div>
    );
};

interface AlignedDataDisplayProps {
    data: {
        label: string | ReactNode;
        value: string | ReactNode;
    }[];
    useGrid?: boolean;
}

export const AlignedDataDisplay = ({
    data,
    useGrid = false,
}: AlignedDataDisplayProps) => {
    if (useGrid) {
        return (
            <div className="w-full text-sm grid gap-2">
                {data
                    .filter((i) => !!i)
                    .map((row, index) => (
                        <div key={index} className="grid grid-cols-1 gap-1">
                            <div className="font-semibold text-ae-slate-900">
                                {row.label}
                            </div>
                            <div className="break-words">
                                {row.value || "-"}
                            </div>
                        </div>
                    ))}
            </div>
        );
    }

    return (
        <table className="w-full text-sm border-separate border-spacing-1">
            <tbody>
                {data.map((row, index) => (
                    <tr key={index}>
                        <td className="text-ae-slate-900">{row.label}</td>
                        <td className="font-semibold line-clamp-3 break-all w-full">
                            {row.value || "-"}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};
